import type { Topic } from '../../../grades/components/GradeLessonPage';

export const coordinateGeometry: Topic = {
  id: 'coordinate-geometry',
  title: 'Coordinate Geometry',
  icon: 'ri-line-chart-line',
  color: 'violet',
  skills: [
    {
      id: 'number-line-midpoint',
      title: 'Number Line & Midpoint',
      standards: ['ACT Math: Coordinate Geometry', 'Points on Number Line'],
      explanation: 'The number line represents real numbers as points on a line, with positive numbers to the right of zero and negative numbers to the left. The distance between two points a and b is |b - a|. The midpoint between two points a and b is (a + b)/2. On a coordinate plane, the midpoint between points (x₁, y₁) and (x₂, y₂) is ((x₁ + x₂)/2, (y₁ + y₂)/2). Understanding number lines, distance, and midpoint formulas is fundamental for ACT coordinate geometry questions.',
      questions: [
        {
          question: 'Find the distance between -5 and 8 on a number line',
          solution: 'Step 1: Use distance formula: |b - a|\nStep 2: |8 - (-5)|\nStep 3: |8 + 5|\nStep 4: |13| = 13',
          answer: '13'
        },
        {
          question: 'Find the midpoint between -3 and 7 on a number line',
          solution: 'Step 1: Use midpoint formula: (a + b)/2\nStep 2: (-3 + 7)/2\nStep 3: 4/2\nStep 4: = 2',
          answer: '2'
        },
        {
          question: 'Find the midpoint between points (2, 5) and (8, 13)',
          solution: 'Step 1: Use formula: ((x₁ + x₂)/2, (y₁ + y₂)/2)\nStep 2: x-coordinate: (2 + 8)/2 = 10/2 = 5\nStep 3: y-coordinate: (5 + 13)/2 = 18/2 = 9\nStep 4: Midpoint: (5, 9)',
          answer: '(5, 9)'
        },
        {
          question: 'What is the distance between 12 and -4?',
          solution: 'Step 1: Distance = |12 - (-4)|\nStep 2: = |12 + 4|\nStep 3: = |16|\nStep 4: = 16',
          answer: '16'
        },
        {
          question: 'Find the midpoint of (-6, 2) and (4, -8)',
          solution: 'Step 1: x-coordinate: (-6 + 4)/2 = -2/2 = -1\nStep 2: y-coordinate: (2 + (-8))/2 = -6/2 = -3\nStep 3: Midpoint: (-1, -3)',
          answer: '(-1, -3)'
        },
        {
          question: 'If the midpoint between x and 10 is 3, find x',
          solution: 'Step 1: Use midpoint formula: (x + 10)/2 = 3\nStep 2: Multiply both sides by 2: x + 10 = 6\nStep 3: Subtract 10: x = -4',
          answer: 'x = -4'
        },
        {
          question: 'Find the distance between points (1, 2) and (1, 8)',
          solution: 'Step 1: Same x-coordinate, so vertical distance\nStep 2: Distance = |8 - 2|\nStep 3: = 6',
          answer: '6'
        },
        {
          question: 'What point is halfway between (0, 0) and (10, 6)?',
          solution: 'Step 1: x-coordinate: (0 + 10)/2 = 5\nStep 2: y-coordinate: (0 + 6)/2 = 3\nStep 3: Midpoint: (5, 3)',
          answer: '(5, 3)'
        },
        {
          question: 'Find the distance between -15 and -3',
          solution: 'Step 1: Distance = |-3 - (-15)|\nStep 2: = |-3 + 15|\nStep 3: = |12|\nStep 4: = 12',
          answer: '12'
        },
        {
          question: 'If M(4, y) is the midpoint of A(2, 3) and B(6, 9), find y',
          solution: 'Step 1: Midpoint y-coordinate: (3 + 9)/2 = y\nStep 2: 12/2 = y\nStep 3: y = 6',
          answer: 'y = 6'
        }
      ]
    },
    {
      id: 'distance-formula',
      title: 'Distance Formula',
      standards: ['ACT Math: Coordinate Geometry', 'Distance Between Points'],
      explanation: 'The distance formula calculates the distance between two points (x₁, y₁) and (x₂, y₂) on a coordinate plane: d = √((x₂ - x₁)² + (y₂ - y₁)²). This formula is derived from the Pythagorean theorem. The distance is always positive. Special cases include horizontal lines (same y-coordinates) where d = |x₂ - x₁|, and vertical lines (same x-coordinates) where d = |y₂ - y₁|. Understanding and applying the distance formula is essential for ACT coordinate geometry problems.',
      questions: [
        {
          question: 'Find the distance between (3, 4) and (7, 1)',
          solution: 'Step 1: d = √((x₂ - x₁)² + (y₂ - y₁)²)\nStep 2: = √((7 - 3)² + (1 - 4)²)\nStep 3: = √(4² + (-3)²)\nStep 4: = √(16 + 9) = √25 = 5',
          answer: '5'
        },
        {
          question: 'Find the distance between (-2, 5) and (4, -3)',
          solution: 'Step 1: d = √((4 - (-2))² + (-3 - 5)²)\nStep 2: = √((6)² + (-8)²)\nStep 3: = √(36 + 64)\nStep 4: = √100 = 10',
          answer: '10'
        },
        {
          question: 'What is the distance from the origin to (6, 8)?',
          solution: 'Step 1: Origin is (0, 0)\nStep 2: d = √((6 - 0)² + (8 - 0)²)\nStep 3: = √(36 + 64)\nStep 4: = √100 = 10',
          answer: '10'
        },
        {
          question: 'Find the distance between (1, 1) and (4, 5)',
          solution: 'Step 1: d = √((4 - 1)² + (5 - 1)²)\nStep 2: = √(3² + 4²)\nStep 3: = √(9 + 16)\nStep 4: = √25 = 5',
          answer: '5'
        },
        {
          question: 'Find the distance between (-3, -2) and (5, 4)',
          solution: 'Step 1: d = √((5 - (-3))² + (4 - (-2))²)\nStep 2: = √(8² + 6²)\nStep 3: = √(64 + 36)\nStep 4: = √100 = 10',
          answer: '10'
        },
        {
          question: 'What is the distance between (0, 5) and (12, 0)?',
          solution: 'Step 1: d = √((12 - 0)² + (0 - 5)²)\nStep 2: = √(144 + 25)\nStep 3: = √169\nStep 4: = 13',
          answer: '13'
        },
        {
          question: 'Find the distance between (2, -3) and (2, 5)',
          solution: 'Step 1: Same x-coordinate (vertical line)\nStep 2: d = |5 - (-3)|\nStep 3: = |8|\nStep 4: = 8',
          answer: '8'
        },
        {
          question: 'Find the distance between (-4, 7) and (8, 7)',
          solution: 'Step 1: Same y-coordinate (horizontal line)\nStep 2: d = |8 - (-4)|\nStep 3: = |12|\nStep 4: = 12',
          answer: '12'
        },
        {
          question: 'What is the distance from (-1, -1) to (2, 3)?',
          solution: 'Step 1: d = √((2 - (-1))² + (3 - (-1))²)\nStep 2: = √(3² + 4²)\nStep 3: = √(9 + 16)\nStep 4: = √25 = 5',
          answer: '5'
        },
        {
          question: 'Find the distance between (5, 2) and (1, -1)',
          solution: 'Step 1: d = √((1 - 5)² + (-1 - 2)²)\nStep 2: = √((-4)² + (-3)²)\nStep 3: = √(16 + 9)\nStep 4: = √25 = 5',
          answer: '5'
        }
      ]
    },
    {
      id: 'slope-equations',
      title: 'Slope & Equations of Lines',
      standards: ['ACT Math: Coordinate Geometry', 'Linear Equations'],
      explanation: 'The slope of a line measures its steepness and direction: m = (y₂ - y₁)/(x₂ - x₁). Positive slope rises left to right, negative slope falls, zero slope is horizontal, and undefined slope is vertical. The slope-intercept form is y = mx + b, where m is slope and b is y-intercept. Point-slope form is y - y₁ = m(x - x₁). Standard form is Ax + By = C. Converting between forms and finding equations given different information are key ACT skills.',
      questions: [
        {
          question: 'Find the slope of the line through (2, 3) and (6, 11)',
          solution: 'Step 1: m = (y₂ - y₁)/(x₂ - x₁)\nStep 2: = (11 - 3)/(6 - 2)\nStep 3: = 8/4\nStep 4: = 2',
          answer: '2'
        },
        {
          question: 'What is the slope of the line y = -3x + 7?',
          solution: 'Step 1: Equation is in slope-intercept form y = mx + b\nStep 2: Coefficient of x is the slope\nStep 3: m = -3',
          answer: '-3'
        },
        {
          question: 'Write the equation of a line with slope 4 and y-intercept -2',
          solution: 'Step 1: Use slope-intercept form: y = mx + b\nStep 2: m = 4, b = -2\nStep 3: y = 4x - 2',
          answer: 'y = 4x - 2'
        },
        {
          question: 'Find the slope of the line through (-1, 5) and (3, -3)',
          solution: 'Step 1: m = (-3 - 5)/(3 - (-1))\nStep 2: = -8/4\nStep 3: = -2',
          answer: '-2'
        },
        {
          question: 'What is the equation of a line through (2, 5) with slope 3?',
          solution: 'Step 1: Use point-slope form: y - y₁ = m(x - x₁)\nStep 2: y - 5 = 3(x - 2)\nStep 3: y - 5 = 3x - 6\nStep 4: y = 3x - 1',
          answer: 'y = 3x - 1'
        },
        {
          question: 'Find the slope of a horizontal line',
          solution: 'Step 1: Horizontal lines have the form y = c\nStep 2: No change in y-values\nStep 3: m = 0/Δx = 0',
          answer: '0'
        },
        {
          question: 'What is the slope of the line 2x + 3y = 12?',
          solution: 'Step 1: Convert to slope-intercept form\nStep 2: 3y = -2x + 12\nStep 3: y = (-2/3)x + 4\nStep 4: Slope m = -2/3',
          answer: '-2/3'
        },
        {
          question: 'Find the equation of the line through (0, -4) and (3, 2)',
          solution: 'Step 1: Find slope: m = (2 - (-4))/(3 - 0) = 6/3 = 2\nStep 2: y-intercept is -4 (point on y-axis)\nStep 3: y = 2x - 4',
          answer: 'y = 2x - 4'
        },
        {
          question: 'What is the slope of a vertical line?',
          solution: 'Step 1: Vertical lines have the form x = c\nStep 2: Δx = 0 (no change in x)\nStep 3: m = Δy/0 = undefined',
          answer: 'Undefined'
        },
        {
          question: 'Write y - 3 = 2(x + 1) in slope-intercept form',
          solution: 'Step 1: Distribute: y - 3 = 2x + 2\nStep 2: Add 3 to both sides: y = 2x + 5',
          answer: 'y = 2x + 5'
        }
      ]
    },
    {
      id: 'parallel-perpendicular',
      title: 'Parallel & Perpendicular Lines',
      standards: ['ACT Math: Coordinate Geometry', 'Line Relationships'],
      explanation: 'Parallel lines have the same slope but different y-intercepts. They never intersect. If line 1 has slope m₁ and line 2 has slope m₂, they are parallel if m₁ = m₂. Perpendicular lines intersect at right angles (90°). Their slopes are negative reciprocals: if one line has slope m, a perpendicular line has slope -1/m. For example, if m₁ = 2, then m₂ = -1/2 for perpendicular lines. Horizontal and vertical lines are always perpendicular. Understanding these relationships is crucial for ACT coordinate geometry.',
      questions: [
        {
          question: 'What is the slope of a line parallel to y = 5x - 3?',
          solution: 'Step 1: Parallel lines have equal slopes\nStep 2: Given line has slope 5\nStep 3: Parallel line also has slope 5',
          answer: '5'
        },
        {
          question: 'What is the slope of a line perpendicular to y = 2x + 1?',
          solution: 'Step 1: Perpendicular slopes are negative reciprocals\nStep 2: Given slope is 2\nStep 3: Perpendicular slope = -1/2',
          answer: '-1/2'
        },
        {
          question: 'Are lines y = 3x + 2 and y = 3x - 5 parallel, perpendicular, or neither?',
          solution: 'Step 1: Compare slopes\nStep 2: Both have slope 3\nStep 3: Equal slopes → parallel',
          answer: 'Parallel'
        },
        {
          question: 'Find the equation of a line parallel to y = -4x + 7 through (1, 2)',
          solution: 'Step 1: Parallel line has same slope: m = -4\nStep 2: Use point-slope: y - 2 = -4(x - 1)\nStep 3: y - 2 = -4x + 4\nStep 4: y = -4x + 6',
          answer: 'y = -4x + 6'
        },
        {
          question: 'What is the slope perpendicular to a line with slope -3/4?',
          solution: 'Step 1: Perpendicular slope is negative reciprocal\nStep 2: Flip fraction: -3/4 → -4/3\nStep 3: Change sign: -4/3 → 4/3',
          answer: '4/3'
        },
        {
          question: 'Are y = (1/2)x + 3 and y = -2x - 1 perpendicular?',
          solution: 'Step 1: Slopes are 1/2 and -2\nStep 2: Check if negative reciprocals: -1/(1/2) = -2 ✓\nStep 3: Yes, they are perpendicular',
          answer: 'Yes'
        },
        {
          question: 'Find the equation perpendicular to y = x - 4 through (0, 5)',
          solution: 'Step 1: Given slope is 1, perpendicular slope is -1\nStep 2: Point (0, 5) is the y-intercept\nStep 3: y = -x + 5',
          answer: 'y = -x + 5'
        },
        {
          question: 'What is the slope of a line parallel to 2x + 3y = 6?',
          solution: 'Step 1: Convert to slope-intercept: 3y = -2x + 6\nStep 2: y = (-2/3)x + 2\nStep 3: Slope is -2/3\nStep 4: Parallel line has same slope: -2/3',
          answer: '-2/3'
        },
        {
          question: 'Are lines with slopes 5 and -5 perpendicular?',
          solution: 'Step 1: Check if negative reciprocals\nStep 2: -1/5 ≠ -5\nStep 3: Not perpendicular',
          answer: 'No'
        },
        {
          question: 'Find the slope perpendicular to a horizontal line',
          solution: 'Step 1: Horizontal line has slope 0\nStep 2: Perpendicular to horizontal is vertical\nStep 3: Vertical line has undefined slope',
          answer: 'Undefined'
        }
      ]
    },
    {
      id: 'graphing-linear',
      title: 'Graphing Linear Equations',
      standards: ['ACT Math: Coordinate Geometry', 'Graphing Lines'],
      explanation: 'Graphing linear equations involves plotting points and drawing a straight line through them. Key methods include: using slope-intercept form (start at y-intercept, use slope to find more points), using x- and y-intercepts (set x=0 to find y-intercept, set y=0 to find x-intercept), or plotting points from a table of values. The x-intercept is where the line crosses the x-axis (y=0), and the y-intercept is where it crosses the y-axis (x=0). Understanding how to graph lines and identify intercepts is essential for ACT coordinate geometry.',
      questions: [
        {
          question: 'Find the y-intercept of y = 3x - 6',
          solution: 'Step 1: y-intercept occurs when x = 0\nStep 2: y = 3(0) - 6\nStep 3: y = -6\nStep 4: y-intercept is (0, -6)',
          answer: '(0, -6) or -6'
        },
        {
          question: 'Find the x-intercept of y = 2x + 8',
          solution: 'Step 1: x-intercept occurs when y = 0\nStep 2: 0 = 2x + 8\nStep 3: -8 = 2x\nStep 4: x = -4, so x-intercept is (-4, 0)',
          answer: '(-4, 0) or -4'
        },
        {
          question: 'What are the intercepts of 3x + 4y = 12?',
          solution: 'Step 1: x-intercept (y=0): 3x = 12 → x = 4 → (4, 0)\nStep 2: y-intercept (x=0): 4y = 12 → y = 3 → (0, 3)\nStep 3: Intercepts: (4, 0) and (0, 3)',
          answer: '(4, 0) and (0, 3)'
        },
        {
          question: 'Graph y = -2x + 4 using slope and y-intercept',
          solution: 'Step 1: y-intercept is 4, plot (0, 4)\nStep 2: Slope is -2 = -2/1 (down 2, right 1)\nStep 3: From (0, 4), go down 2 and right 1 to (1, 2)\nStep 4: Draw line through (0, 4) and (1, 2)',
          answer: 'Line through (0, 4) with slope -2'
        },
        {
          question: 'Find both intercepts of y = -x + 5',
          solution: 'Step 1: y-intercept (x=0): y = 5 → (0, 5)\nStep 2: x-intercept (y=0): 0 = -x + 5 → x = 5 → (5, 0)\nStep 3: Intercepts: (0, 5) and (5, 0)',
          answer: '(0, 5) and (5, 0)'
        },
        {
          question: 'What is the x-intercept of 2x - 5y = 10?',
          solution: 'Step 1: Set y = 0\nStep 2: 2x - 5(0) = 10\nStep 3: 2x = 10\nStep 4: x = 5, so (5, 0)',
          answer: '(5, 0) or 5'
        },
        {
          question: 'Graph the line through (0, -3) with slope 1/2',
          solution: 'Step 1: Start at y-intercept (0, -3)\nStep 2: Slope 1/2 means up 1, right 2\nStep 3: From (0, -3), go up 1 and right 2 to (2, -2)\nStep 4: Draw line through these points',
          answer: 'Line through (0, -3) and (2, -2)'
        },
        {
          question: 'Find the y-intercept of 4x + 2y = 8',
          solution: 'Step 1: Set x = 0\nStep 2: 4(0) + 2y = 8\nStep 3: 2y = 8\nStep 4: y = 4, so (0, 4)',
          answer: '(0, 4) or 4'
        },
        {
          question: 'What line passes through (0, 0) and (3, 6)?',
          solution: 'Step 1: Find slope: m = (6-0)/(3-0) = 6/3 = 2\nStep 2: y-intercept is 0 (passes through origin)\nStep 3: Equation: y = 2x',
          answer: 'y = 2x'
        },
        {
          question: 'Find the x-intercept of y = 5x - 15',
          solution: 'Step 1: Set y = 0\nStep 2: 0 = 5x - 15\nStep 3: 15 = 5x\nStep 4: x = 3, so (3, 0)',
          answer: '(3, 0) or 3'
        }
      ]
    },
    {
      id: 'graphing-quadratics',
      title: 'Graphing Quadratics & Parabolas',
      standards: ['ACT Math: Coordinate Geometry', 'Parabolas'],
      explanation: 'A quadratic function has the form f(x) = ax² + bx + c, and its graph is a parabola. If a > 0, the parabola opens upward; if a < 0, it opens downward. The vertex is the highest or lowest point, found at x = -b/(2a). The y-intercept is c (when x=0). The axis of symmetry is the vertical line x = -b/(2a). The x-intercepts (roots) are found by solving ax² + bx + c = 0. Understanding parabola features—vertex, axis of symmetry, direction, and intercepts—is crucial for ACT coordinate geometry.',
      questions: [
        {
          question: 'Does y = 2x² - 4x + 1 open upward or downward?',
          solution: 'Step 1: Check coefficient of x² term\nStep 2: a = 2\nStep 3: Since a > 0, parabola opens upward',
          answer: 'Upward'
        },
        {
          question: 'Find the vertex of y = x² - 6x + 5',
          solution: 'Step 1: x-coordinate: x = -b/(2a) = -(-6)/(2·1) = 6/2 = 3\nStep 2: y-coordinate: y = (3)² - 6(3) + 5 = 9 - 18 + 5 = -4\nStep 3: Vertex: (3, -4)',
          answer: '(3, -4)'
        },
        {
          question: 'What is the axis of symmetry for y = -x² + 4x - 3?',
          solution: 'Step 1: Axis of symmetry: x = -b/(2a)\nStep 2: a = -1, b = 4\nStep 3: x = -4/(2·(-1)) = -4/(-2) = 2\nStep 4: Axis: x = 2',
          answer: 'x = 2'
        },
        {
          question: 'Find the y-intercept of y = 3x² - 2x + 7',
          solution: 'Step 1: y-intercept occurs when x = 0\nStep 2: y = 3(0)² - 2(0) + 7\nStep 3: y = 7\nStep 4: y-intercept: (0, 7)',
          answer: '(0, 7) or 7'
        },
        {
          question: 'Does y = -5x² + 10x - 1 have a maximum or minimum?',
          solution: 'Step 1: Check coefficient of x²\nStep 2: a = -5 < 0\nStep 3: Opens downward → has a maximum',
          answer: 'Maximum'
        },
        {
          question: 'Find the vertex of y = (x - 2)² + 3',
          solution: 'Step 1: Vertex form: y = (x - h)² + k\nStep 2: Vertex is (h, k)\nStep 3: h = 2, k = 3\nStep 4: Vertex: (2, 3)',
          answer: '(2, 3)'
        },
        {
          question: 'What is the axis of symmetry for y = x² + 8x + 12?',
          solution: 'Step 1: x = -b/(2a)\nStep 2: a = 1, b = 8\nStep 3: x = -8/(2·1) = -8/2 = -4\nStep 4: Axis: x = -4',
          answer: 'x = -4'
        },
        {
          question: 'Find the vertex of y = -2x² + 8x - 5',
          solution: 'Step 1: x = -b/(2a) = -8/(2·(-2)) = -8/(-4) = 2\nStep 2: y = -2(2)² + 8(2) - 5 = -8 + 16 - 5 = 3\nStep 3: Vertex: (2, 3)',
          answer: '(2, 3)'
        },
        {
          question: 'What is the y-intercept of y = x² - 4x?',
          solution: 'Step 1: Set x = 0\nStep 2: y = (0)² - 4(0)\nStep 3: y = 0\nStep 4: y-intercept: (0, 0)',
          answer: '(0, 0) or 0'
        },
        {
          question: 'Does y = 3(x + 1)² - 2 open up or down?',
          solution: 'Step 1: Coefficient of squared term is 3\nStep 2: 3 > 0\nStep 3: Opens upward',
          answer: 'Upward'
        }
      ]
    },
    {
      id: 'circles-coordinate',
      title: 'Circles on Coordinate Plane',
      standards: ['ACT Math: Coordinate Geometry', 'Circle Equations'],
      explanation: 'The standard form equation of a circle with center (h, k) and radius r is (x - h)² + (y - k)² = r². A circle centered at the origin has equation x² + y² = r². To find the center and radius from an equation, identify h, k, and r. To write an equation, substitute the center coordinates and radius. The distance from the center to any point on the circle equals the radius. Understanding circle equations and how to identify center and radius is important for ACT coordinate geometry.',
      questions: [
        {
          question: 'Find the center and radius of (x - 3)² + (y + 2)² = 25',
          solution: 'Step 1: Standard form: (x - h)² + (y - k)² = r²\nStep 2: h = 3, k = -2, r² = 25\nStep 3: Center: (3, -2)\nStep 4: Radius: r = √25 = 5',
          answer: 'Center (3, -2), radius 5'
        },
        {
          question: 'Write the equation of a circle with center (0, 0) and radius 4',
          solution: 'Step 1: Center at origin: x² + y² = r²\nStep 2: r = 4, so r² = 16\nStep 3: Equation: x² + y² = 16',
          answer: 'x² + y² = 16'
        },
        {
          question: 'Find the center of x² + (y - 5)² = 9',
          solution: 'Step 1: Compare to (x - h)² + (y - k)² = r²\nStep 2: x² = (x - 0)², so h = 0\nStep 3: (y - 5)², so k = 5\nStep 4: Center: (0, 5)',
          answer: '(0, 5)'
        },
        {
          question: 'What is the radius of (x + 1)² + (y - 4)² = 36?',
          solution: 'Step 1: r² = 36\nStep 2: r = √36\nStep 3: r = 6',
          answer: '6'
        },
        {
          question: 'Write the equation of a circle with center (-2, 3) and radius 7',
          solution: 'Step 1: Use (x - h)² + (y - k)² = r²\nStep 2: h = -2, k = 3, r = 7\nStep 3: (x - (-2))² + (y - 3)² = 7²\nStep 4: (x + 2)² + (y - 3)² = 49',
          answer: '(x + 2)² + (y - 3)² = 49'
        },
        {
          question: 'Find the center and radius of x² + y² = 100',
          solution: 'Step 1: This is centered at origin\nStep 2: Center: (0, 0)\nStep 3: r² = 100, so r = 10',
          answer: 'Center (0, 0), radius 10'
        },
        {
          question: 'What is the diameter of (x - 5)² + (y + 1)² = 64?',
          solution: 'Step 1: r² = 64, so r = 8\nStep 2: Diameter = 2r\nStep 3: = 2(8) = 16',
          answer: '16'
        },
        {
          question: 'Find the center of (x + 4)² + (y + 6)² = 1',
          solution: 'Step 1: (x - h)² + (y - k)² = r²\nStep 2: (x + 4)² = (x - (-4))², so h = -4\nStep 3: (y + 6)² = (y - (-6))², so k = -6\nStep 4: Center: (-4, -6)',
          answer: '(-4, -6)'
        },
        {
          question: 'Write the equation: center (1, -3), radius 5',
          solution: 'Step 1: (x - h)² + (y - k)² = r²\nStep 2: h = 1, k = -3, r = 5\nStep 3: (x - 1)² + (y - (-3))² = 25\nStep 4: (x - 1)² + (y + 3)² = 25',
          answer: '(x - 1)² + (y + 3)² = 25'
        },
        {
          question: 'What is the radius of x² + (y + 2)² = 49?',
          solution: 'Step 1: r² = 49\nStep 2: r = √49\nStep 3: r = 7',
          answer: '7'
        }
      ]
    },
    {
      id: 'transformations',
      title: 'Transformations',
      standards: ['ACT Math: Coordinate Geometry', 'Translations, Reflections, Rotations'],
      explanation: 'Transformations move or change figures on the coordinate plane. A translation slides a figure: (x, y) → (x + a, y + b). A reflection flips a figure over a line: over x-axis (x, y) → (x, -y), over y-axis (x, y) → (-x, y), over y=x (x, y) → (y, x). A rotation turns a figure around a point: 90° counterclockwise around origin (x, y) → (-y, x), 180° (x, y) → (-x, -y). A dilation scales a figure: (x, y) → (kx, ky) where k is the scale factor. Understanding these transformations is essential for ACT coordinate geometry.',
      questions: [
        {
          question: 'Translate point (3, 5) by moving 4 units right and 2 units down',
          solution: 'Step 1: Right 4: add 4 to x-coordinate\nStep 2: Down 2: subtract 2 from y-coordinate\nStep 3: (3 + 4, 5 - 2)\nStep 4: (7, 3)',
          answer: '(7, 3)'
        },
        {
          question: 'Reflect point (4, -2) over the x-axis',
          solution: 'Step 1: Reflection over x-axis: (x, y) → (x, -y)\nStep 2: Keep x-coordinate: 4\nStep 3: Negate y-coordinate: -(-2) = 2\nStep 4: (4, 2)',
          answer: '(4, 2)'
        },
        {
          question: 'Reflect point (-3, 5) over the y-axis',
          solution: 'Step 1: Reflection over y-axis: (x, y) → (-x, y)\nStep 2: Negate x-coordinate: -(-3) = 3\nStep 3: Keep y-coordinate: 5\nStep 4: (3, 5)',
          answer: '(3, 5)'
        },
        {
          question: 'Rotate point (2, 3) 90° counterclockwise around the origin',
          solution: 'Step 1: 90° CCW rotation: (x, y) → (-y, x)\nStep 2: New x = -y = -3\nStep 3: New y = x = 2\nStep 4: (-3, 2)',
          answer: '(-3, 2)'
        },
        {
          question: 'Dilate point (6, 8) by scale factor 1/2 from the origin',
          solution: 'Step 1: Dilation: (x, y) → (kx, ky)\nStep 2: k = 1/2\nStep 3: (6 · 1/2, 8 · 1/2)\nStep 4: (3, 4)',
          answer: '(3, 4)'
        },
        {
          question: 'Rotate point (5, 0) 180° around the origin',
          solution: 'Step 1: 180° rotation: (x, y) → (-x, -y)\nStep 2: (-5, 0)',
          answer: '(-5, 0)'
        },
        {
          question: 'Translate (2, -3) by vector <-1, 4>',
          solution: 'Step 1: Add vector components to coordinates\nStep 2: x: 2 + (-1) = 1\nStep 3: y: -3 + 4 = 1\nStep 4: (1, 1)',
          answer: '(1, 1)'
        },
        {
          question: 'Reflect (7, 2) over the line y = x',
          solution: 'Step 1: Reflection over y = x: (x, y) → (y, x)\nStep 2: Swap coordinates\nStep 3: (2, 7)',
          answer: '(2, 7)'
        },
        {
          question: 'Dilate (-4, 6) by scale factor 3 from the origin',
          solution: 'Step 1: (x, y) → (3x, 3y)\nStep 2: (3(-4), 3(6))\nStep 3: (-12, 18)',
          answer: '(-12, 18)'
        },
        {
          question: 'What is the image of (0, 5) reflected over the x-axis?',
          solution: 'Step 1: (x, y) → (x, -y)\nStep 2: (0, -5)',
          answer: '(0, -5)'
        }
      ]
    },
    {
      id: 'graphing-inequalities-coord',
      title: 'Graphing Inequalities',
      standards: ['ACT Math: Coordinate Geometry', 'Inequality Regions'],
      explanation: 'Graphing inequalities on the coordinate plane involves shading regions. For linear inequalities like y > mx + b, first graph the boundary line (dashed for > or <, solid for ≥ or ≤), then shade the appropriate region. Test a point (often the origin if not on the line) to determine which side to shade. For y > expression, shade above; for y < expression, shade below. Systems of inequalities require finding the overlapping shaded region. Understanding how to graph inequalities and identify solution regions is important for ACT coordinate geometry.',
      questions: [
        {
          question: 'Should the boundary line for y < 2x + 1 be solid or dashed?',
          solution: 'Step 1: Check inequality symbol\nStep 2: < means "less than" (not equal to)\nStep 3: Use dashed line (points on line not included)',
          answer: 'Dashed'
        },
        {
          question: 'For y ≥ -x + 3, which side of the line should be shaded?',
          solution: 'Step 1: y ≥ means y-values greater than or equal to line\nStep 2: Greater y-values are above the line\nStep 3: Shade above the line',
          answer: 'Above'
        },
        {
          question: 'Is (2, 5) in the solution region of y > 2x?',
          solution: 'Step 1: Substitute: 5 > 2(2)?\nStep 2: 5 > 4?\nStep 3: Yes, true',
          answer: 'Yes'
        },
        {
          question: 'Graph y ≤ 3: solid or dashed line?',
          solution: 'Step 1: ≤ includes equality\nStep 2: Points on line y = 3 are included\nStep 3: Use solid line',
          answer: 'Solid'
        },
        {
          question: 'For x > -2, which region is shaded?',
          solution: 'Step 1: x > -2 means x-values greater than -2\nStep 2: Greater x-values are to the right\nStep 3: Shade right of vertical line x = -2',
          answer: 'Right of x = -2'
        },
        {
          question: 'Is (0, 0) a solution to y < x + 2?',
          solution: 'Step 1: Substitute: 0 < 0 + 2?\nStep 2: 0 < 2?\nStep 3: Yes, true',
          answer: 'Yes'
        },
        {
          question: 'For y > -3x - 1, shade above or below the line?',
          solution: 'Step 1: y > means y-values greater than line\nStep 2: Greater y-values are above\nStep 3: Shade above',
          answer: 'Above'
        },
        {
          question: 'Should y ≥ 5 have a solid or dashed boundary?',
          solution: 'Step 1: ≥ includes equality\nStep 2: Use solid line',
          answer: 'Solid'
        },
        {
          question: 'Is (-1, 4) in the region y ≤ 2x + 5?',
          solution: 'Step 1: Substitute: 4 ≤ 2(-1) + 5?\nStep 2: 4 ≤ -2 + 5?\nStep 3: 4 ≤ 3? No, false',
          answer: 'No'
        },
        {
          question: 'For x ≤ 4, which side is shaded?',
          solution: 'Step 1: x ≤ 4 means x-values less than or equal to 4\nStep 2: Smaller x-values are to the left\nStep 3: Shade left of x = 4 (including line)',
          answer: 'Left of x = 4'
        }
      ]
    },
    {
      id: 'conics-overview',
      title: 'Conics Overview',
      standards: ['ACT Math: Coordinate Geometry', 'Conic Sections'],
      explanation: 'Conic sections are curves formed by intersecting a plane with a cone: circles, ellipses, parabolas, and hyperbolas. A circle has equation (x-h)² + (y-k)² = r². An ellipse has equation (x-h)²/a² + (y-k)²/b² = 1. A parabola has forms like y = a(x-h)² + k or x = a(y-k)² + h. A hyperbola has equation (x-h)²/a² - (y-k)²/b² = 1 or the reverse. Each conic has a center (or vertex for parabolas) and specific properties. Understanding basic conic equations and how to identify them is useful for ACT coordinate geometry.',
      questions: [
        {
          question: 'Identify the conic: x² + y² = 16',
          solution: 'Step 1: Both variables squared with same coefficient\nStep 2: Form: x² + y² = r²\nStep 3: This is a circle',
          answer: 'Circle'
        },
        {
          question: 'Identify the conic: y = 2x² - 3',
          solution: 'Step 1: Only one variable is squared\nStep 2: Form: y = ax² + bx + c\nStep 3: This is a parabola',
          answer: 'Parabola'
        },
        {
          question: 'Identify the conic: x²/9 + y²/16 = 1',
          solution: 'Step 1: Both variables squared with different denominators\nStep 2: Plus sign between terms\nStep 3: Form: x²/a² + y²/b² = 1\nStep 4: This is an ellipse',
          answer: 'Ellipse'
        },
        {
          question: 'What is the center of the circle (x-2)² + (y+3)² = 25?',
          solution: 'Step 1: Form: (x-h)² + (y-k)² = r²\nStep 2: h = 2, k = -3\nStep 3: Center: (2, -3)',
          answer: '(2, -3)'
        },
        {
          question: 'Identify the conic: x²/4 - y²/9 = 1',
          solution: 'Step 1: Both variables squared\nStep 2: Minus sign between terms\nStep 3: Form: x²/a² - y²/b² = 1\nStep 4: This is a hyperbola',
          answer: 'Hyperbola'
        },
        {
          question: 'Is x² + y² = 0 a circle?',
          solution: 'Step 1: Form matches circle equation\nStep 2: But r² = 0, so r = 0\nStep 3: This is a degenerate circle (single point)\nStep 4: Technically yes, but radius is 0',
          answer: 'Yes (degenerate, point)'
        },
        {
          question: 'Identify: (x-1)²/25 + (y+2)²/9 = 1',
          solution: 'Step 1: Both squared, different denominators\nStep 2: Plus sign\nStep 3: This is an ellipse',
          answer: 'Ellipse'
        },
        {
          question: 'What conic is x = y² + 2y - 3?',
          solution: 'Step 1: Only y is squared, x is not\nStep 2: This is a parabola\nStep 3: Opens horizontally (x = ...)',
          answer: 'Parabola'
        },
        {
          question: 'Identify: 4x² + 4y² = 36',
          solution: 'Step 1: Both squared with same coefficient\nStep 2: Divide by 4: x² + y² = 9\nStep 3: This is a circle',
          answer: 'Circle'
        },
        {
          question: 'What is the vertex of y = (x-3)² + 5?',
          solution: 'Step 1: Vertex form: y = (x-h)² + k\nStep 2: h = 3, k = 5\nStep 3: Vertex: (3, 5)',
          answer: '(3, 5)'
        }
      ]
    }
  ]
};